// The Text block's HTML — a rich paragraph or an embed — made safe to
// v-html, in ONE place for every template (Blocks/TextBlock.vue,
// Trailhead/ThText.vue).
//
// It is SANITIZED, not trusted. A customer site is served from
// app.trailsiq.com/site/{slug} as well as from its own domain, so a
// <script> here would run on OUR origin, as whoever opened the page
// signed in, platform admins included.
//
// Two layers, and the second is what server rendering needs:
//  - the server sanitizes every Text block on the way out and puts the
//    result beside the original as `html_safe`
//    (App\Support\Website\SiteBlockHtml, from PublicSiteController);
//  - in a browser, DOMPurify runs again over that (or over the raw html
//    in the builder's canvas, which never went through the server).
//
// With no DOM — resources/js/ssr.js under node — DOMPurify has no
// `sanitize` at all and `document` does not exist; calling either threw
// and took the whole page out of server rendering. There the server's
// string is printed as it is, and without one the words are printed as
// escaped text: never the raw html.
//
// The markup the server drew is the markup the visitor keeps: Vue does
// not re-apply v-html when it hydrates, so running DOMPurify over the
// SAME sanitized input in the browser cannot flip the page.
import DOMPurify from 'dompurify';

const RICH = {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 's', 'u', 'a', 'ul', 'ol', 'li', 'blockquote', 'h2', 'h3', 'span', 'code'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i,
};

const EMBED = {
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target', 'loading', 'referrerpolicy'],
    FORBID_TAGS: ['style', 'form', 'input', 'button', 'textarea', 'select', 'object', 'embed', 'base', 'meta', 'link'],
    RETURN_DOM_FRAGMENT: true,
};

function canPurify() {
    return typeof window !== 'undefined' && typeof document !== 'undefined'
        && typeof DOMPurify.sanitize === 'function';
}

function escapeText(html) {
    return String(html || '')
        .replace(/<(script|style|template|noscript|textarea|select|iframe)\b[\s\S]*?<\/\1\s*>/gi, ' ')
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function source(item) {
    return typeof item?.html_safe === 'string' ? item.html_safe : String(item?.html || '');
}

// A rich paragraph is what the builder's toolbar can produce and
// nothing more: no iframes, no embeds — those are the Embed item's job,
// and a paragraph pasted from somewhere else must not grow one.
export function safeRichHtml(item) {
    if (!canPurify()) return typeof item?.html_safe === 'string' ? item.html_safe : escapeText(item?.html);
    return DOMPurify.sanitize(source(item), RICH);
}

// An embed keeps ordinary markup and https iframes (video, maps, forms
// hosted elsewhere). Script, event handlers, javascript: links and form
// controls are what goes.
export function safeEmbedHtml(item) {
    if (!canPurify()) return typeof item?.html_safe === 'string' ? item.html_safe : escapeText(item?.html);
    const clean = DOMPurify.sanitize(source(item), EMBED);
    clean.querySelectorAll('iframe').forEach((f) => {
        if (!/^https:\/\//i.test(f.getAttribute('src') || '')) f.remove();
    });
    const box = document.createElement('div');
    box.appendChild(clean);

    return box.innerHTML;
}
